import { useMemo, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import {
  buildCitBulkAttachPreview,
  citMissingScAttachmentRows,
  collectCitBulkPdfFiles,
  downloadCitBulkAttachTemplate,
  parseCitBulkSheet,
  runCitBulkAttachImport,
  type CitBulkAttachPreview,
  type CitBulkSheetRow,
} from '../../lib/citBulkAttachImport';
import type { ApplicationSubmission, CustomiseItemsTransferFormData } from '../../types/applicationForm';

type Props = {
  open: boolean;
  rows: ApplicationSubmission[];
  onClose: () => void;
  onImported: () => void | Promise<void>;
};

export function CitBulkAttachImportModal({ open, rows, onClose, onImported }: Props) {
  const sheetInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [sheetName, setSheetName] = useState<string | null>(null);
  const [pdfLabel, setPdfLabel] = useState<string | null>(null);
  const [sheetRows, setSheetRows] = useState<CitBulkSheetRow[]>([]);
  const [filesByKey, setFilesByKey] = useState<Map<string, File>>(new Map());
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [resultErrors, setResultErrors] = useState<string[]>([]);

  const missing = useMemo(() => citMissingScAttachmentRows(rows), [rows]);

  const preview: CitBulkAttachPreview = useMemo(
    () => buildCitBulkAttachPreview(rows, sheetRows, filesByKey),
    [rows, sheetRows, filesByKey],
  );

  function reset() {
    setSheetName(null);
    setPdfLabel(null);
    setSheetRows([]);
    setFilesByKey(new Map());
    setParseErrors([]);
    setParseWarnings([]);
    setProgress(null);
    setResultMessage(null);
    setResultErrors([]);
    if (sheetInputRef.current) sheetInputRef.current.value = '';
    if (pdfInputRef.current) pdfInputRef.current.value = '';
  }

  async function handleSheet(file: File) {
    setSheetName(file.name);
    setResultMessage(null);
    setResultErrors([]);
    try {
      const parsed = await parseCitBulkSheet(file);
      setSheetRows(parsed.rows);
      setParseErrors(parsed.errors);
    } catch (err) {
      setSheetRows([]);
      setParseErrors([err instanceof Error ? err.message : 'Failed to read spreadsheet.']);
    }
  }

  async function handlePdfs(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    setPdfLabel(
      files.length === 1 ? files[0].name : `${files.length} files`,
    );
    setResultMessage(null);
    setResultErrors([]);
    try {
      const collected = await collectCitBulkPdfFiles(files);
      setFilesByKey(collected.filesByKey);
      setParseWarnings(collected.warnings);
    } catch (err) {
      setFilesByKey(new Map());
      setParseWarnings([]);
      setParseErrors((prev) => [
        ...prev,
        err instanceof Error ? err.message : 'Failed to read PDF / zip.',
      ]);
    }
  }

  async function handleImport() {
    if (!preview.plans.length || preview.errors.length) return;
    setBusy(true);
    setResultMessage(null);
    setResultErrors([]);
    try {
      const result = await runCitBulkAttachImport(preview.plans, (done, total) => {
        setProgress({ done, total });
      });
      const parts = [
        result.updated ? `${result.updated} updated` : '',
        result.skipped ? `${result.skipped} skipped` : '',
      ].filter(Boolean);
      setResultMessage(parts.length ? `Done: ${parts.join(', ')}.` : 'Nothing updated.');
      setResultErrors(result.errors);
      if (result.updated) await onImported();
    } catch (err) {
      setResultErrors([err instanceof Error ? err.message : 'Bulk fill failed.']);
    } finally {
      setBusy(false);
    }
  }

  const blocking = [...parseErrors, ...preview.errors];
  const canImport = preview.plans.length > 0 && blocking.length === 0 && !busy;

  return (
    <Modal
      title="Temporary bulk fill (no SC file yet)"
      open={open}
      onClose={() => {
        if (busy) return;
        reset();
        onClose();
      }}
      extraWide
    >
      <div className="cit-bulk">
        <p className="page-lead">
          Only rows without an SC PDF ({missing.length}). Spreadsheet sets Deposit arrangement
          and Status; PDFs attach by filename (e.g. <span className="mono">2026SOC10003.pdf</span>{' '}
          → 2026/SOC/10/003).
        </p>

        <div className="cit-bulk__actions">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => void downloadCitBulkAttachTemplate(rows)}
          >
            Download template
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => sheetInputRef.current?.click()}
          >
            {sheetName ? 'Change spreadsheet' : 'Choose spreadsheet'}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => pdfInputRef.current?.click()}
          >
            {pdfLabel ? 'Change PDFs / zip' : 'Choose PDFs or zip'}
          </button>
          <input
            ref={sheetInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleSheet(file);
            }}
          />
          <input
            ref={pdfInputRef}
            type="file"
            accept=".pdf,.zip,application/pdf,application/zip"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) void handlePdfs(e.target.files);
            }}
          />
        </div>

        <p className="field-hint">
          {sheetName ? `Sheet: ${sheetName}` : 'No spreadsheet'}
          {' · '}
          {pdfLabel ? `Files: ${pdfLabel} (${filesByKey.size} matched names)` : 'No PDFs'}
        </p>

        {blocking.length ? (
          <div className="alert alert-error">
            {blocking.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        ) : null}
        {parseWarnings.length || preview.warnings.length ? (
          <div className="cit-bulk__warnings">
            {[...parseWarnings, ...preview.warnings].map((line) => (
              <p key={line} className="field-hint">
                {line}
              </p>
            ))}
          </div>
        ) : null}
        {resultMessage ? <div className="alert alert-success">{resultMessage}</div> : null}
        {resultErrors.length ? (
          <div className="alert alert-error">
            {resultErrors.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        ) : null}

        {preview.plans.length ? (
          <p className="cit-bulk__summary">
            Ready: {preview.plans.length} of {missing.length} rows without SC file.
            {progress ? ` · ${progress.done}/${progress.total}` : ''}
          </p>
        ) : (
          <p className="field-hint">
            {missing.length === 0
              ? 'Every CIT row already has an SC file.'
              : 'Upload a template and/or PDFs to see the fill preview.'}
          </p>
        )}

        {preview.plans.length ? (
          <div className="cit-bulk__table-wrap">
            <table className="cit-bulk__table">
              <thead>
                <tr>
                  <th>Sales confirmation</th>
                  <th>Customer</th>
                  <th>PDF</th>
                  <th>Arrangement</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.plans.map((plan) => {
                  const data = plan.submission.form_data as CustomiseItemsTransferFormData;
                  return (
                    <tr key={plan.submission.id}>
                      <td className="mono">{plan.scNo}</td>
                      <td>
                        {[data.customer_code, data.customer_name].filter(Boolean).join(' — ') ||
                          '—'}
                      </td>
                      <td>{plan.file ? plan.file.name : '—'}</td>
                      <td>
                        {plan.needsDeposit === true
                          ? 'Yes'
                          : plan.needsDeposit === false
                            ? 'No'
                            : '—'}
                      </td>
                      <td>{plan.status || (plan.needsDeposit === false ? 'Completed (auto)' : '—')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="cit-bulk__footer">
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => reset()}>
            Clear
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!canImport}
            onClick={() => void handleImport()}
          >
            {busy
              ? `Updating… ${progress ? `${progress.done}/${progress.total}` : ''}`
              : `Apply to ${preview.plans.length} row${preview.plans.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
